import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { FileText, ThumbsUp, Calendar, MapPin, Clock } from "lucide-react";
import { api } from "../api";
import { Card, PageHeader, formatDate } from "../ui";
import { useAuth } from "../AuthContext";

export default function AssociateDashboard() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const isAssociate = user?.role === "associado";
  const roleLabel =
    user?.role === "conselheiro"
      ? "Conselheiro(a)"
      : user?.role === "diretor"
      ? "Diretor(a)"
      : "associado(a)";

  useEffect(() => {
    api.get("/portal/dashboard").then((r) => setData(r.data)).catch(() => {});
  }, []);

  return (
    <div data-testid="associate-dashboard">
      <PageHeader
        title={`Olá, ${user?.name?.split(" ")[0] || "membro"}.`}
        subtitle={
          isAssociate
            ? "Resumo das suas atividades no Portal do Associado da ABLEMP."
            : `Resumo institucional do Portal ABLEMP — perfil ${roleLabel}.`
        }
      />

      <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
        <StatCard
          to="/portal/meus-arquivos"
          icon={FileText}
          label={isAssociate ? "Meus arquivos" : "Arquivos"}
          value={data?.files_count ?? "—"}
          accent="#002776"
          testid="card-files"
        />
        <StatCard
          to="/portal/reunioes"
          icon={ThumbsUp}
          label={isAssociate ? "Pautas votadas" : "Pautas pendentes"}
          value={isAssociate ? (data?.voted_count ?? "—") : "—"}
          accent="#FEDF00"
          dark
          testid="card-voted"
        />
        <StatCard
          to="/portal/reunioes"
          icon={Calendar}
          label="Última ata"
          value={formatDate(data?.latest_meeting?.meeting_date)}
          accent="#475569"
          testid="card-meeting"
        />
      </div>

      <div className="grid lg:grid-cols-2 gap-4 mt-6">
        <Card className="p-6" testid="dashboard-events">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-[11px] uppercase tracking-[0.25em] text-slate-500">
                Calendário ABLEMP
              </div>
              <h3 className="mt-2 font-serif-display text-2xl">
                Próximos eventos
              </h3>
            </div>
            <Calendar size={22} className="text-[#002776]" strokeWidth={1.4} />
          </div>
          {(!data?.upcoming_events || data.upcoming_events.length === 0) ? (
            <div className="mt-5 border border-dashed border-slate-200 rounded-sm py-8 text-center text-sm text-slate-500">
              Nenhum evento agendado no momento.
            </div>
          ) : (
            <ul className="mt-5 divide-y divide-slate-200">
              {data.upcoming_events.map((ev) => (
                <li
                  key={ev.id}
                  data-testid={`dash-event-${ev.id}`}
                  className="py-3 flex items-start gap-4"
                >
                  <DateChip iso={ev.event_date} />
                  <div className="flex-1 min-w-0">
                    <div className="font-medium leading-tight">{ev.title}</div>
                    <div className="mt-1 text-xs text-slate-500 flex flex-wrap items-center gap-3">
                      {ev.event_time && (
                        <span className="inline-flex items-center gap-1">
                          <Clock size={11} /> {ev.event_time}
                        </span>
                      )}
                      {ev.location && (
                        <span className="inline-flex items-center gap-1">
                          <MapPin size={11} /> {ev.location}
                        </span>
                      )}
                    </div>
                    {ev.description && (
                      <p className="mt-1 text-sm text-slate-600 line-clamp-2">
                        {ev.description}
                      </p>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </Card>

        <Card className="p-6" testid="dashboard-meetings">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-[11px] uppercase tracking-[0.25em] text-slate-500">
                Agenda institucional
              </div>
              <h3 className="mt-2 font-serif-display text-2xl">
                Próximas reuniões
              </h3>
            </div>
            <Calendar size={22} className="text-[#FEDF00]" strokeWidth={1.4} />
          </div>
          {(!data?.upcoming_meetings || data.upcoming_meetings.length === 0) ? (
            <div className="mt-5 border border-dashed border-slate-200 rounded-sm py-8 text-center text-sm text-slate-500">
              Nenhuma reunião agendada no momento.
            </div>
          ) : (
            <ul className="mt-5 divide-y divide-slate-200">
              {data.upcoming_meetings.map((m) => (
                <li
                  key={m.id}
                  data-testid={`dash-meeting-${m.id}`}
                  className="py-3 flex items-start gap-4"
                >
                  <DateChip iso={m.meeting_date} />
                  <div className="flex-1 min-w-0">
                    <Link
                      to="/portal/reunioes"
                      className="font-medium leading-tight hover:text-[#002776]"
                    >
                      {m.title}
                    </Link>
                    <div className="mt-1 text-xs text-slate-500 flex flex-wrap items-center gap-3">
                      {(m.start_time || m.end_time) && (
                        <span className="inline-flex items-center gap-1">
                          <Clock size={11} />
                          {m.start_time || "—"}
                          {m.end_time ? ` – ${m.end_time}` : ""}
                        </span>
                      )}
                      {m.location && (
                        <span className="inline-flex items-center gap-1">
                          <MapPin size={11} /> {m.location}
                        </span>
                      )}
                      {m.is_online && (
                        <span className="inline-flex items-center gap-1 text-emerald-700 font-medium">
                          ● Online
                        </span>
                      )}
                    </div>
                    {m.is_online && m.online_link && (
                      <a
                        href={m.online_link}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid={`dash-meeting-link-${m.id}`}
                        className="mt-1 inline-block text-xs text-[#002776] underline hover:text-[#001a5a]"
                      >
                        Entrar na reunião →
                      </a>
                    )}
                    {m.description && (
                      <p className="mt-1 text-sm text-slate-600 line-clamp-2">
                        {m.description}
                      </p>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </Card>
      </div>

      <Card className="p-6 mt-4">
        <div className="text-[11px] uppercase tracking-[0.25em] text-slate-500">
          Última ata registrada
        </div>
        {data?.latest_meeting ? (
          <>
            <h4 className="mt-2 font-serif-display text-xl leading-tight">
              {data.latest_meeting.title}
            </h4>
            <div className="text-xs text-slate-500 mt-1">
              {formatDate(data.latest_meeting.meeting_date)}
            </div>
            <p className="mt-3 text-sm text-slate-600 line-clamp-4 max-w-3xl">
              {data.latest_meeting.summary || "—"}
            </p>
          </>
        ) : (
          <p className="mt-3 text-sm text-slate-500">
            Nenhuma ata registrada ainda.
          </p>
        )}
      </Card>

      <Card className="p-6 mt-4">
        <div className="text-[11px] uppercase tracking-[0.25em] text-slate-500">
          Próximos passos
        </div>
        <h3 className="mt-2 font-serif-display text-2xl">
          Participe da pauta da próxima reunião
        </h3>
        <p className="mt-3 text-sm text-slate-600 leading-relaxed max-w-3xl">
          Sugira temas, vote nas propostas dos demais associados, consulte o
          Estatuto e acompanhe as atas das reuniões anteriores.
        </p>
        <div className="mt-5 flex gap-2 flex-wrap">
          <a
            href="/portal/reunioes"
            className="px-4 py-2 bg-[#0A0F14] text-white text-[11px] uppercase tracking-[0.22em] hover:bg-[#002776]"
          >
            Ir para reuniões
          </a>
          <a
            href="/portal/estatuto"
            className="px-4 py-2 border border-slate-300 text-[11px] uppercase tracking-[0.22em] hover:border-[#002776]"
          >
            Ver estatuto
          </a>
        </div>
      </Card>
    </div>
  );
}

const StatCard = ({ icon: Icon, label, value, accent, dark, testid, to }) => (
  <Link
    to={to}
    data-testid={testid}
    className="block bg-white border border-slate-200 rounded-md p-5 hover:border-[#0A0F14] hover:shadow-sm transition-all"
  >
    <div className="flex items-start justify-between">
      <div
        className="h-10 w-10 grid place-items-center rounded-sm"
        style={{
          background: accent + (dark ? "" : "12"),
          color: dark ? "#0A0F14" : accent,
        }}
      >
        <Icon size={18} strokeWidth={1.6} />
      </div>
    </div>
    <div className="mt-4 font-serif-display text-3xl leading-none">{value}</div>
    <div className="mt-1 text-[11px] uppercase tracking-[0.22em] text-slate-500">
      {label}
    </div>
  </Link>
);

const DateChip = ({ iso }) => {
  const d = iso ? new Date(iso + "T00:00:00") : null;
  const day = d ? String(d.getDate()).padStart(2, "0") : "—";
  const month = d
    ? d.toLocaleDateString("pt-BR", { month: "short" }).replace(".", "")
    : "—";
  return (
    <div className="h-12 w-12 grid place-items-center border border-slate-300 rounded-sm bg-white shrink-0">
      <div className="text-center leading-none">
        <div className="font-serif-display text-lg text-[#002776]">{day}</div>
        <div className="text-[9px] uppercase tracking-[0.15em] text-slate-500">
          {month}
        </div>
      </div>
    </div>
  );
};
